<template>
  <div class="soar-container flex flex-col gap-y-4">
    <h1 class="text-xl md:text-2xl text-primary font-medium border-b border-brand-gray-300">
      {{ $t('applicationTrackingMenu') }}
    </h1>
    <div class="w-full flex justify-center">
      <div class="rounded-md border border-brand-gray-300 p-2 w-full md:w-96">
        <Form
          v-if="!isLoading"
          class="flex flex-col gap-3"
          v-slot="{ resetForm }"
          @submit="(values) => onSubmit(values, resetForm)"
        >
          <Field
            :rules="
              (value) => {
                return requiredRule(value) && minChar(15, value)
              }
            "
            validate-on-input
            validate-on-change
            validate-on-blur
            name="requestNumber"
            v-slot="{ field, errors: errs }"
          >
            <TextInput
              :field="field"
              :errors="errs"
              :label="$t('referenceNumber')"
              mask="numberOnly"
              label-class="w-full"
              class="w-full"
              direction="flex-col"
            />
          </Field>
          <fieldset class="border-2 border-primary rounded p-4 flex flex-col gap-y-5">
            <legend class="px-2">{{ $t('verifyApplicant') }}</legend>
            <Field :rules="requiredRule" name="recaptchaResponse" v-slot="{ handleChange }">
              <GRecaptcha
                :key="rando"
                ref="gRecaptchaRef"
                class="flex justify-center"
                @successCallback="(args) => handleChange('recaptchaResponse', args)"
              ></GRecaptcha>
            </Field>
          </fieldset>
          <div class="w-full flex justify-end">
            <button
              class="text-center rounded-md bg-primary/80 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
            >
              <div class="p-2 bg-primary rounded-s-md">
                <faMagnifyingGlass class="w-4 fill-white"></faMagnifyingGlass>
              </div>
              <span class="w-full text-center">
                {{ $t('Search') }}
              </span>
              <div class="p-0.5"></div>
            </button>
          </div>
        </Form>
      </div>
    </div>

    <ResultView v-if="!isFetching" :request="request" />
  </div>
</template>

<script setup>
import faMagnifyingGlass from '@/assets/icons/faMagnifyingGlass.svg?component'
import client_lookups from '@/assets/json/clientLookups.json'
import client_config from '@/assets/json/client_config.json'
import GRecaptcha from '@/components/GRecaptcha/index.vue'
import TextInput from '@/components/SimpleForm/TextInput.vue'
import axiosInstance from '@/services'
import { Field, Form } from 'vee-validate'
import { onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import ResultView from '@/views/services/public/application-tracking/ResultView.vue'

const i18n = useI18n()
const isLoading = ref(true)
const isFetching = ref(true)
const rando = ref()
const initialValues = ref()

const { allowAnonymousToDownloadTheEmiratesIDRegistrationForm } = client_config
const {
  module: { ICA, ICAandResi },
  requestActions: { approved }
} = client_lookups

const request = reactive({
  entity: null,
  isAllowedToDownloadEid: false,
  showHeaderResult: false,
  isApproved: false
})

function requiredRule(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return true
}

function minChar(char, value) {
  if (!value) return i18n.t('INVALID_REQUIRED')

  if (value?.trim().length < char) {
    return i18n.t('INVALID_MIN_CHAR', [char])
  }

  return true
}

onMounted(async () => {
  isLoading.value = false
})

const onSubmit = async (form) => {
  isFetching.value = true

  const body = {
    requestDraftNumber: form.requestNumber,
    recaptchaResponse: form.recaptchaResponse,
    requestType: 1,
    isUsingCaptcha: true,
    isNotFreezone: true
  }

  const response = await axiosInstance.post('/api/landing/trackingRequests', body)
  const { requestDraftInfo, requestActions } = response

  const servicesToSkip = [476, 478, 545, 479]
  if (servicesToSkip.includes(requestDraftInfo.serviceTransId)) {
    return
  }

  request.entity = requestDraftInfo

  const { moduleId, requestNumber, userName } = request.entity

  if (!requestNumber) {
    request.showHeaderResult = false
    return
  }

  const isICARelated = [ICA, ICAandResi].includes(moduleId)
  if (allowAnonymousToDownloadTheEmiratesIDRegistrationForm && isICARelated) {
    const path = `/api/landing/isAllowToDownloadEid/${requestNumber}`
    request.isAllowedToDownloadEid = await axiosInstance.get(path)
  }

  request.showHeaderResult = !!requestNumber
  request.applicationHistory = requestActions
  request.isApproved = requestActions.some((x) => x.actionId == approved)
  rando.value = Math.random()

  if (request.entity?.serviceTransId == 162) {
    const params = {
      userName
    }
    request.isNotFreezone = await axiosInstance.get('/api/user/checkIfNotFreeZone', { params })
  }

  isFetching.value = false
}
</script>

<style lang="scss" scoped></style>
